import { z } from 'zod';

/**
 * `pending-perf-trend-dashboard` v1 — wire types for the long-term
 * perf-trend dashboard. v1 uses **TimescaleDB-on-Postgres** as the
 * sink; the other adapters (Prometheus, Grafana Cloud, InfluxDB,
 * Datadog) ride the same shapes in follow-up PRs.
 */

export const PerfMetricSchema = z.enum([
  'runDurationMs',
  'stepDurationMs',
  'lcp',
  'cls',
  'inp',
  'fcp',
  'ttfb',
]);
export type PerfMetric = z.infer<typeof PerfMetricSchema>;

export const PerfBucketSchema = z.enum(['daily', 'weekly']);
export type PerfBucket = z.infer<typeof PerfBucketSchema>;

export const PerfTrendPointSchema = z.object({
  /** ISO date (YYYY-MM-DD). */
  date: z.string(),
  p50: z.number(),
  p95: z.number(),
  p99: z.number(),
  sampleCount: z.number().int().nonnegative(),
});
export type PerfTrendPoint = z.infer<typeof PerfTrendPointSchema>;

export const PerfTrendQuerySchema = z.object({
  flowId: z.string().uuid().optional(),
  metric: PerfMetricSchema,
  stepIndex: z.coerce.number().int().nonnegative().optional(),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'from must be a YYYY-MM-DD date'),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'to must be a YYYY-MM-DD date'),
  bucket: PerfBucketSchema.default('daily'),
});
export type PerfTrendQuery = z.infer<typeof PerfTrendQuerySchema>;

export const PerfTrendResponseSchema = z.object({
  metric: PerfMetricSchema,
  bucket: PerfBucketSchema,
  points: z.array(PerfTrendPointSchema),
});
export type PerfTrendResponse = z.infer<typeof PerfTrendResponseSchema>;

/**
 * The set of sinks the operator can pick in admin-config. v1 ships
 * `timescale` end-to-end and stubs the rest.
 */
export const PerfSinkNameSchema = z.enum([
  'timescale',
  'prometheus',
  'grafana-cloud',
  'influxdb',
  'datadog',
  'victoriametrics',
]);
export type PerfSinkName = z.infer<typeof PerfSinkNameSchema>;

/**
 * Admin-config slice for the perf trend dashboard. Default is OFF
 * so a fresh install never starts writing to an unconfigured sink.
 */
export const PerfTrendsConfigSchema = z.object({
  enabled: z.boolean().default(false),
  sink: PerfSinkNameSchema.default('timescale'),
  postgresMirrorEnabled: z.boolean().default(true),
  retentionDays: z.number().int().positive().default(365),
  readEnabled: z.boolean().default(true),
  worker: z
    .object({
      runHourUtc: z.number().int().min(0).max(23).default(5),
      lookbackHours: z.number().int().positive().default(24),
    })
    .default({ runHourUtc: 5, lookbackHours: 24 }),
});
export type PerfTrendsConfig = z.infer<typeof PerfTrendsConfigSchema>;
